import { useState, useCallback } from "react";
import type { Template, SendForReviewForm } from "../../types/template";
import type { UserOption } from "../../types/user";

type SendForReviewViewProps = {
  template: Template;
  users: UserOption[];
  usersError?: string | null;
  isSubmitting?: boolean;
  submitError?: string | null;
  /** Post-commit Graph upload status (FR-005b), same copy as Contract Review notifications. */
  graphPostCommitBanner?: string | null;
  onSendForReview: (form: SendForReviewForm) => Promise<void>;
  onBack: () => void;
};

export const SendForReviewView = ({
  template,
  users,
  usersError = null,
  isSubmitting = false,
  submitError = null,
  graphPostCommitBanner = null,
  onSendForReview,
  onBack
}: SendForReviewViewProps) => {
  const [reviewerId, setReviewerId] = useState("");
  const [note, setNote] = useState("");

  const selectedUser = users.find((u) => u.id === reviewerId);
  const isFormValid = !!reviewerId.trim();

  const handleSendForReview = useCallback(async () => {
    if (!isFormValid || isSubmitting) return;

    const form: SendForReviewForm = {
      reviewerId,
      reviewerName: selectedUser?.name ?? "",
      note: note.trim()
    };
    await onSendForReview(form);
  }, [
    reviewerId,
    selectedUser?.name,
    note,
    isFormValid,
    isSubmitting,
    onSendForReview
  ]);

  const graphPostCommitIsFailure = graphPostCommitBanner?.includes("please save manually") ?? false;

  return (
    <div className="space-y-4">
      {graphPostCommitBanner ? (
        <p
          className={
            graphPostCommitIsFailure
              ? "rounded-lg border border-destructive/30 px-3 py-2 text-sm text-destructive"
              : "rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-foreground"
          }
          role="status"
        >
          {graphPostCommitBanner}
        </p>
      ) : null}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Send for Review</h2>
        <p className="text-sm text-muted-foreground">{template.name}</p>
      </div>

      <div className="space-y-3">
        <div>
          <label htmlFor="reviewer-name" className="mb-1 block text-sm font-medium text-foreground">
            Reviewer Name <span className="text-destructive">*</span>
          </label>
          <select
            id="reviewer-name"
            value={reviewerId}
            onChange={(e) => setReviewerId(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm"
          >
            <option value="">Select reviewer</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
          {usersError && (
            <p className="mt-1 text-xs text-muted-foreground">{usersError}</p>
          )}
        </div>

        <div>
          <label htmlFor="note" className="mb-1 block text-sm font-medium text-foreground">
            Note / Comment
          </label>
          <textarea
            id="note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isSubmitting}
            placeholder="Add any notes or instructions for the reviewer (optional)"
            className="w-full rounded-lg border border-[var(--border)] bg-input-background px-3 py-2 text-sm disabled:opacity-60"
          />
        </div>
      </div>

      {submitError && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {submitError}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => {
            void handleSendForReview();
          }}
          disabled={!isFormValid || isSubmitting}
          className="flex-1 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Sending…" : "Send for Review"}
        </button>
      </div>
    </div>
  );
};
